import React, { useContext, useRef, useState } from "react";
import { Formik, Form } from "formik";
import Box from "@mui/material/Box";
import { useQuery } from "@apollo/client/react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import DialogContent from "@mui/material/DialogContent";
import Button from "@mui/material/Button";
import Backdrop from "@mui/material/Backdrop";
import LinearProgress from "@mui/material/LinearProgress";
import DialogActions from "@mui/material/DialogActions";
import { useNavigate, useLocation } from "react-router";
import CircularProgress from "@mui/material/CircularProgress";

import LocationInfo from "./LocationInfo";
import LocationSnapshot from "./LocationSnapshot";
import LocationActivity from "./LocationActivity";
import UnsavedChangesDialog from "../../../modals/UnsavedChanges/UnsavedChangesDialog";
import LocationAssetDetailsModal from "./LocationAssetDetailsModal";
import useQueryParams from "../../../../hooks/useQueryParams";
import useUnsavedChanges from "../../../../hooks/useUnsavedChanges";
import {
  WAREHOUSE_LOCATION,
  MOBILE_WAREHOUSE_LOCATION,
} from "../../../../constants/graphql/queries";
import {
  WAREHOUSE_LOCATION_UPDATE,
  MOBILE_WAREHOUSE_LOCATION_UPDATE,
  START_WAREHOUSE_LOCATION_AUDIT_MUTATION,
  COMPLETE_WAREHOUSE_LOCATION_AUDIT_MUTATION,
} from "../../../../constants/graphql/mutations";
import { SelectLocationContext } from "../../../../views/capture/RackLocations/WarehouseModal";
import { WarehouseContext } from "../../../..//views/warehouse/WarehouseList";
import useMutation from "../../../../hooks/useMutation";
import useNotification from "../../../../hooks/notification";
import {
  AUDIT_ID_PARAM,
  AUDIT_MODE_PARAM,
  AUDIT_PARAMS,
  clearLocationParams,
  LOC_ASSET_ID_PARAM,
} from "../../../../views/warehouse/locationParams";
import { toString as errorsToString } from "../../../../utils/parseErrors";
import SelectLocationPositionModal from "../../../../views/capture/RackLocations/StorageLocationAutocomplete/SelectLocationPositionModal";
import {
  getLocationGroupName,
  removeSlotFromLocationName,
} from "../../../../views/capture/RackLocations/StorageLocationAutocomplete/utils";
import { WAREHOUSE_LOCATIONS_COLLECTION_INVENTORY_TRANSACTION } from "../../../../constants/graphql/queries";
import { fullWidthDialog } from "src/constants/dialog";

const DisableTransitionComponent = ({ children }) => children;
const formatMutationErrors = errors => {
  if (!Array.isArray(errors) || errors.length === 0) {
    return "";
  }

  return errors
    .map(error => error?.message)
    .filter(Boolean)
    .join(", ");
};

export default function EditLocationDetails({ entity, value, onClose, modal = false }) {
  const [isEditing, setIsEditing] = useState(false);
  const [selectLocationPosition, setSelectLocationPosition] = useState(null);
  const [modalAssetId, setModalAssetId] = useState(null);
  const formikRef = useRef(null);
  const selectLocation = useContext(SelectLocationContext);
  const warehouse = useContext(WarehouseContext);
  const { notifySuccess, notifyError } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = useQueryParams();
  const auditModeEnabled = queryParams.get(AUDIT_MODE_PARAM) === "true";
  const currentAuditId = queryParams.get(AUDIT_ID_PARAM);

  const setAuditQueryParams = ({ auditMode, auditId } = {}, useReplace = false) => {
    const nextParams = new URLSearchParams(location.search);
    if (auditMode !== undefined) {
      auditMode ? nextParams.set(AUDIT_MODE_PARAM, "true") : nextParams.delete(AUDIT_MODE_PARAM);
    }
    if (auditId !== undefined) {
      auditId ? nextParams.set(AUDIT_ID_PARAM, auditId) : nextParams.delete(AUDIT_ID_PARAM);
    }
    useReplace
      ? navigate({ search: nextParams.toString() }, { replace: true })
      : navigate({ search: nextParams.toString() });
  };
  const isMobileLocation = entity.__typename === "MobileWarehouseLocation";
  const query = isMobileLocation ? MOBILE_WAREHOUSE_LOCATION : WAREHOUSE_LOCATION;
  const { data = {}, loading, refetch: refetchLocationDetails } = useQuery(query, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    variables: { id: entity.id },
  });
  const warehouseLocation = isMobileLocation
    ? data.mobileWarehouseLocation
    : data.warehouseLocation;
  const locationAssetId = modal
    ? modalAssetId
    : new URLSearchParams(location.search).get(LOC_ASSET_ID_PARAM);

  const [updateWarehouseLocation] = useMutation(WAREHOUSE_LOCATION_UPDATE);
  const [updateMobileLocation] = useMutation(MOBILE_WAREHOUSE_LOCATION_UPDATE);
  const [startWarehouseLocationAudit, { loading: isStartingAudit }] = useMutation(
    START_WAREHOUSE_LOCATION_AUDIT_MUTATION
  );
  const [completeWarehouseLocationAudit, { loading: isCompletingAudit }] = useMutation(
    COMPLETE_WAREHOUSE_LOCATION_AUDIT_MUTATION
  );

  const baseLocationName =
    warehouseLocation && warehouseLocation.slot
      ? removeSlotFromLocationName({
          locationName: warehouseLocation.locationName,
          slot: warehouseLocation.slot,
        })
      : warehouseLocation?.locationName;
  const locationGroupName = warehouseLocation ? getLocationGroupName(warehouseLocation) : null;

  const { data: { warehouseLocationsCollection = {} } = {} } = useQuery(
    WAREHOUSE_LOCATIONS_COLLECTION_INVENTORY_TRANSACTION,
    {
      variables: {
        warehouseId: warehouse?.id,
        search: baseLocationName,
        skipVirtual: true,
        matchLocationName: true,
        expandSlotSiblings: true,
        limit: 10,
      },
      skip: !warehouseLocation || isMobileLocation || !baseLocationName,
      fetchPolicy: "network-only",
      nextFetchPolicy: "cache-first",
    }
  );

  const locationSlots = React.useMemo(() => {
    if (isMobileLocation) {
      return warehouseLocation?.warehouseLocations || [];
    }
    const collection = warehouseLocationsCollection?.collection || [];
    return collection.filter(loc => getLocationGroupName(loc) === locationGroupName);
  }, [isMobileLocation, warehouseLocation, warehouseLocationsCollection, locationGroupName]);

  const handleSelectLocation = () => {
    if (locationSlots && locationSlots.length > 1) {
      setSelectLocationPosition(locationSlots);
      return;
    }

    // Build proper nested structure with all required fields
    selectLocation({
      ...warehouseLocation,
      warehouseId: warehouse.warehouseId,
      warehouse: {
        id: warehouse.id,
        warehouse_id: warehouse.warehouseId,
        name: warehouse.name,
      },
      locations: [warehouseLocation],
      __typename: entity.__typename,
    });
  };

  const handleSelectPositionsSubmit = locations => {
    if (!locations || locations.length === 0) return;

    // Build proper nested structure with all required fields
    selectLocation({
      warehouseId: warehouse.warehouseId,
      locationName: locations[0].locationName,
      id: locations[0].id,
      locationId: locations[0].locationId,
      zone: locations[0].zone,
      warehouse: {
        id: warehouse.id,
        warehouse_id: warehouse.warehouseId,
        name: warehouse.name,
      },
      locations: locations,
      __typename: entity.__typename,
    });

    setSelectLocationPosition(null);
  };

  const runUpdate = (values, overrides, onFailure) => {
    if (isMobileLocation) {
      return updateMobileLocation({
        variables: {
          id: entity.id,
          locationName: values.locationName,
          searchTerms: values.searchTerms || "",
          locationType: values.locationType,
          sqFtOfSlot: parseInt(values.sqFtOfSlot, 10),
          zone: values.zone,
          dimensions: values.dimensions,
          active: values.active,
          ...overrides,
        },
        onFailure,
      });
    }

    return updateWarehouseLocation({
      variables: {
        id: entity.id,
        locationName: values.locationName,
        searchTerms: values.searchTerms || "",
        slotGroupKey: values.slotGroupKey || "",
        locationType: values.locationType,
        sqFtOfSlot: parseInt(values.sqFtOfSlot, 10),
        zone: values.zone,
        aisle: values.aisle || "",
        rack: values.rack || "",
        shelf: values.shelf || "",
        slot: values.slot || "",
        dimensions: values.dimensions,
        active: values.active,
        ...overrides,
      },
      onFailure,
    });
  };

  // useMutation resolves even when the update fails, so collect the failure and
  // throw it; callers rely on a rejection to know the save did not happen.
  const performUpdate = async (values, overrides = {}) => {
    let mutationFailure = null;
    const response = await runUpdate(values, overrides, apiErrors => {
      mutationFailure = apiErrors;
    });
    if (mutationFailure) {
      throw new Error(
        mutationFailure.submit || errorsToString(mutationFailure) || "Failed to update location."
      );
    }
    return response;
  };

  // Returns whether the save succeeded so the unsaved-changes prompt can keep
  // the panel open when it did not.
  const handleSubmit = async values => {
    try {
      await performUpdate(values);
      setIsEditing(false);
      return true;
    } catch (error) {
      console.error("Error updating location:", error);
      notifyError(error.message);
      return false;
    }
  };

  const handleToggleActive = async (values, nextActive) => {
    try {
      await performUpdate(values, { active: nextActive });
      setIsEditing(false);
    } catch (error) {
      console.error("Error toggling location active state:", error);
      notifyError(error.message);
    }
  };

  const getInitialValues = () => {
    if (!warehouseLocation) return {};

    const baseValues = {
      locationName: warehouseLocation.locationName || "",
      searchTerms: warehouseLocation.searchTerms || "",
      slotGroupKey: warehouseLocation.slotGroupKey || "",
      locationType: warehouseLocation.locationType || "",
      zone: warehouseLocation.zone || "",
      sqFtOfSlot: warehouseLocation.sqFtOfSlot || 0,
      dimensions: warehouseLocation.dimensions || [],
      active: warehouseLocation.active || false,
    };

    if (!isMobileLocation) {
      return {
        ...baseValues,
        aisle: warehouseLocation.aisle || "",
        rack: warehouseLocation.rack || "",
        shelf: warehouseLocation.shelf || "",
        slot: warehouseLocation.slot || "",
        active: warehouseLocation.active || false,
      };
    }

    return baseValues;
  };

  const openAssetDetails = assetId => {
    if (!assetId) {
      return;
    }

    if (modal) {
      setModalAssetId(assetId);
      return;
    }

    const queryParams = new URLSearchParams(location.search);
    queryParams.set(LOC_ASSET_ID_PARAM, assetId);
    queryParams.delete("addItemStep");
    navigate({ search: queryParams.toString() });
  };

  const closeAssetDetails = () => {
    if (modal) {
      setModalAssetId(null);
      return;
    }

    const queryParams = new URLSearchParams(location.search);
    if (!queryParams.has(LOC_ASSET_ID_PARAM)) {
      return;
    }
    queryParams.delete(LOC_ASSET_ID_PARAM);
    navigate({ search: queryParams.toString() });
  };

  // Outside a modal, WarehouseTable's onClose clears every location param in one
  // navigation. In a modal it only resets local state, so drop the audit params
  // here.
  const performClose = () => {
    if (modal) {
      closeAssetDetails();
      if (AUDIT_PARAMS.some(name => queryParams.has(name))) {
        const nextParams = clearLocationParams(location.search, AUDIT_PARAMS);
        navigate({ search: nextParams.toString() }, { replace: true });
      }
    }
    onClose();
  };

  // Audit-mode edits save as they happen, so only the Location Info form can
  // hold unsaved changes.
  const {
    attemptClose: handleClose,
    confirmDiscard,
    cancelDiscard,
    confirmSave,
    showDialog,
    saving,
  } = useUnsavedChanges({
    onClose: performClose,
    isDirtyFn: () => isEditing && !!formikRef.current?.dirty,
    onSave: async () => {
      if (!(await handleSubmit(formikRef.current?.values))) {
        throw new Error("Location Info was not saved.");
      }
    },
  });

  const startAudit = async () => {
    if (!warehouseLocation?.id || isStartingAudit || isCompletingAudit) {
      return null;
    }

    let mutationFailure = null;

    const response = await startWarehouseLocationAudit({
      variables: isMobileLocation
        ? { mobileWarehouseLocationId: warehouseLocation.id }
        : { warehouseLocationId: warehouseLocation.id },
      onFailure: apiErrors => {
        mutationFailure = apiErrors;
      },
    });

    const responsePayload = response?.data?.startWarehouseLocationAudit;
    const responseErrorMessage = formatMutationErrors(responsePayload?.errors);
    const auditId = responsePayload?.audit?.id;

    if (mutationFailure || responseErrorMessage || !auditId) {
      notifyError(
        mutationFailure?.submit ||
          responseErrorMessage ||
          "Failed to start warehouse location audit."
      );
      return null;
    }

    notifySuccess("Warehouse location audit started.");
    return auditId;
  };

  const handleAuditModeChange = async enabled => {
    if (enabled) {
      setAuditQueryParams({ auditMode: true });
      const auditId = await startAudit();
      if (!auditId) return;
      setAuditQueryParams({ auditMode: true, auditId });
    } else {
      setAuditQueryParams({ auditMode: false, auditId: null });
    }
  };

  const handleCompleteAudit = async () => {
    if (!currentAuditId || isCompletingAudit || isStartingAudit) {
      return;
    }

    let mutationFailure = null;

    const response = await completeWarehouseLocationAudit({
      variables: { auditId: currentAuditId },
      onFailure: apiErrors => {
        mutationFailure = apiErrors;
      },
    });

    const responsePayload = response?.data?.completeWarehouseLocationAudit;
    const responseErrorMessage = formatMutationErrors(responsePayload?.errors);

    if (mutationFailure || responseErrorMessage || !responsePayload?.audit?.id) {
      notifyError(
        mutationFailure?.submit ||
          responseErrorMessage ||
          "Failed to complete warehouse location audit."
      );
      return;
    }

    notifySuccess("Warehouse location audit completed.");
    setAuditQueryParams({ auditMode: false, auditId: null });
    refetchLocationDetails();
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={getInitialValues()}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      <Form>
        <Box underline="none" sx={{ cursor: "pointer" }} color="primary.main" className="unfocus">
          {value || ""}
        </Box>
        <Dialog
          open
          fullWidth
          maxWidth="md"
          onClose={handleClose}
          TransitionComponent={DisableTransitionComponent}
          slots={{ backdrop: Backdrop }}
          sx={fullWidthDialog}
          slotProps={{
            backdrop: { sx: { transition: "none !important" } },
          }}
        >
          <DialogTitle sx={{ background: "#f4f6f8" }}>
            <Typography
              align="center"
              fontSize={{ xs: 18, md: 24 }}
              fontWeight={400}
              color="textPrimary"
            >
              Location Details for{" "}
              <Box component="span" display="inline" fontWeight={500} color="textPrimary">
                {warehouseLocation?.locationName}
              </Box>
            </Typography>
            <IconButton
              onClick={handleClose}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ background: "#f4f6f8" }}>
            {loading ? (
              <LinearProgress />
            ) : (
              <>
                <LocationInfo
                  key={isEditing ? "editing" : "viewing"}
                  isMobileLocation={isMobileLocation}
                  item={warehouseLocation}
                  isEditing={isEditing}
                  setIsEditing={setIsEditing}
                  onClose={handleClose}
                  onToggleActive={handleToggleActive}
                />
                <Box mt={2} />
              </>
            )}
            <LocationSnapshot
              locationName={warehouseLocation?.locationName}
              isMobileLocation={isMobileLocation}
              entity={entity}
              locationDetails={warehouseLocation}
              onOpenAsset={openAssetDetails}
              onRefetchLocationDetails={refetchLocationDetails}
              auditModeEnabled={auditModeEnabled}
              setAuditModeEnabled={handleAuditModeChange}
              auditModeLoading={isStartingAudit || isCompletingAudit}
            />
            <Box mt={2}>
              <LocationActivity
                subjectType={entity.__typename}
                subjectId={entity.id}
                onOpenAsset={openAssetDetails}
              />
            </Box>
          </DialogContent>
          {selectLocation && (
            <DialogActions sx={{ px: 3, pb: 2, justifyContent: "flex-end" }}>
              <Button variant="contained" onClick={handleSelectLocation}>
                Select Location
              </Button>
            </DialogActions>
          )}
          {!selectLocation && auditModeEnabled && (
            <DialogActions sx={{ px: 3, pb: 2, justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleCompleteAudit}
                disabled={isStartingAudit || isCompletingAudit || !currentAuditId}
                startIcon={
                  isCompletingAudit ? (
                    <CircularProgress size={20} sx={{ color: "primary.main" }} />
                  ) : null
                }
              >
                Complete Audit
              </Button>
            </DialogActions>
          )}
        </Dialog>
        {selectLocationPosition && (
          <SelectLocationPositionModal
            open={!!selectLocationPosition}
            onClose={() => setSelectLocationPosition(null)}
            onSubmit={handleSelectPositionsSubmit}
            locations={selectLocationPosition}
          />
        )}
        <LocationAssetDetailsModal
          assetId={locationAssetId}
          onClose={closeAssetDetails}
          onSaved={refetchLocationDetails}
          variant="wide"
        />
        <UnsavedChangesDialog
          open={showDialog}
          onCancel={cancelDiscard}
          onDiscard={confirmDiscard}
          onSave={confirmSave}
          saving={saving}
        />
      </Form>
    </Formik>
  );
}
